import { HttpWebtrack } from 'utility/http';
import { ConfigActions } from 'utility/http/http-events';
import configureInterceptors from 'utility/http/interceptors/configure-interceptors';
import { store } from '@redux/storeConfig/store';

export const baseUrl = '/conversations';

// ========== Interfaces ==========

// Connections
interface InsertConnectionRequest {
  wabaId: string;
  businessAccountId: string;
  phoneNumberId: string;
  phoneNumber: string;
  displayName?: string;
  accessToken?: string;
  webhookVerifyToken?: string;
  connectedAt?: Date;
}

interface UpdateConnectionRequest {
  conversationsConnectionId: number;
  wabaId?: string;
  businessAccountId?: string;
  phoneNumberId?: string;
  phoneNumber?: string;
  displayName?: string;
  accessToken?: string;
  webhookVerifyToken?: string;
  connectedAt?: Date;
  status: number;
}

interface GetConnectionRequest {
  wabaId?: string;
  phoneNumber?: string;
}

// Contacts
interface InsertContactRequest {
  phoneNumber: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email?: string;
  driverId?: number;
  companyName?: string;
  profilePictureUrl?: string;
  notes?: string;
}

interface UpdateContactRequest {
  conversationsContactId: number;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email?: string;
  driverId?: number;
  companyName?: string;
  profilePictureUrl?: string;
  notes?: string;
  status: number;
}

interface GetContactRequest {
  phoneNumber: string;
}

// Conversations
interface InsertConversationRequest {
  conversationsConnectionId: number;
  recipientType: string;
  recipientId: string;
  conversationsContactId?: number;
  groupName?: string;
  groupDescription?: string;
  lastActivityAt?: Date;
}

interface UpdateConversationRequest {
  conversationId: number;
  conversationsConnectionId?: number;
  conversationsContactId?: number;
  groupName?: string;
  groupDescription?: string;
  unreadCount?: number;
  hasActiveAlert?: boolean;
  lastActivityAt?: string; // ISO string (new Date().toISOString())
  lastMessagePreview?: string;
  lastMessageType?: string;
  lastSenderPhoneNumber?: string;
  status: number;
}

interface GetConversationsByConnectionRequest {
  conversationsConnectionId: number;
}

interface GetConversationsByRecipientRequest {
  recipientId: string;
}

interface GetConversationsTimelineRequest {
  conversationId: number;
  pageSize?: number;
  pageNumber?: number;
}

// Group Members
interface InsertGroupMemberRequest {
  conversationId: number;
  conversationsContactId: number;
  joinedAt?: Date;
  isAdmin?: boolean;
}

interface UpdateGroupMemberRequest {
  conversationsGroupMemberId: number;
  leftAt?: Date;
  isAdmin?: boolean;
  status: number;
}

// Inbound Messages
interface InsertInboundMessageRequest {
  conversationId: number;
  conversationsConnectionId: number;
  senderPhoneNumber: string;
  senderConversationsContactId?: number;
  senderName?: string;
  messageContent?: string;
  mediaType?: string;
  mediaUrl?: string;
  mediaFileName?: string;
  mediaFileTitle?: string;
  whatsAppMessageId: string;
  replyToMessageId?: string;
  receivedAt?: Date;
}

interface UpdateInboundMessageRequest {
  conversationsInboundMessageId: number;
  conversationId: number; // Requerido para notificaciones SignalR
  isRead?: boolean;
  status: number;
}

// Outbound Messages
interface InsertOutboundMessageRequest {
  conversationId: number;
  conversationsConnectionId: number;
  recipientId: string;
  recipientType: string;
  messageContent?: string;
  mediaType?: string;
  mediaUrl?: string;
  mediaFileName?: string;
  mediaFileTitle?: string;
  whatsAppMessageId?: string;
  replyToMessageId?: string;
  messageStatus?: string;
  sentByUserId?: number;
  sentAt?: Date;
}

interface UpdateOutboundMessageRequest {
  conversationsOutboundMessageId: number;
  conversationId: number; // Requerido para notificaciones SignalR
  whatsAppMessageId?: string;
  messageStatus?: string;
  deliveredAt?: Date;
  readAt?: Date;
  failureReason?: string;
  status: number;
}

// Chat Alerts
interface InsertChatAlertRequest {
  conversationId: number;
  relatedPhoneNumber?: string;
  alertType: string;
  alertTitle: string;
  alertDescription?: string;
  eventMetadata?: string;
  createdAt?: Date;
}

interface UpdateChatAlertRequest {
  conversationsChatAlertId: number;
  status: number;
}

// Call Logs
interface InsertCallLogRequest {
  conversationId: number;
  contactPhoneNumber: string;
  callerPhoneNumber: string;
  elevenLabsConversationId?: string;
  elevenLabsAgentId?: string;
  callDirection: string;
  callStatus?: string;
  callDuration?: number;
  scheduledAt?: Date;
  callStartTime?: Date;
  callEndTime?: Date;
  recordingUrl?: string;
  transcriptUrl?: string;
  transcriptText?: string;
  callSummary?: string;
  callCost?: number;
  metadata?: string;
}

interface UpdateCallLogRequest {
  conversationsCallLogId: number;
  elevenLabsConversationId?: string;
  elevenLabsAgentId?: string;
  callStatus?: string;
  callDuration?: number;
  scheduledAt?: Date;
  callStartTime?: Date;
  callEndTime?: Date;
  recordingUrl?: string;
  transcriptUrl?: string;
  transcriptText?: string;
  callSummary?: string;
  callCost?: number;
  metadata?: string;
  status: number;
}

// Call Chat Events
interface InsertCallChatEventRequest {
  conversationId: number;
  conversationsCallLogId: number;
  eventType: string;
  displayMessage?: string;
  scheduledFor?: Date;
  createdAt?: Date;
}

interface UpdateCallChatEventRequest {
  conversationsCallChatEventId: number;
  scheduledFor?: Date;
  eventType?: string;
  status: number;
}

// ========== Services ==========

// #region Connections

export const insertConnectionService = (
  request: InsertConnectionRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/connections/insert`, request);
};

export const updateConnectionService = (
  request: UpdateConnectionRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/connections/update`, request);
};

export const getConnectionService = (
  request: GetConnectionRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  const params = new URLSearchParams();
  if (request.wabaId) params.append('wabaId', request.wabaId);
  if (request.phoneNumber) params.append('phoneNumber', request.phoneNumber);
  return httpWebtrack.get(`${baseUrl}/connections/get?${params.toString()}`);
};

// #endregion

// #region Contacts

export const insertContactService = (
  request: InsertContactRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/contacts/insert`, request);
};

export const updateContactService = (
  request: UpdateContactRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/contacts/update`, request);
};

export const getContactService = (request: GetContactRequest, configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.get(`${baseUrl}/contacts/get?phoneNumber=${request.phoneNumber}`);
};

// #endregion

// #region Conversations

export const insertConversationService = (
  request: InsertConversationRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/insert`, request);
};

export const updateConversationService = (
  request: UpdateConversationRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/update`, request);
};

export const getConversationsByConnectionService = (
  request: GetConversationsByConnectionRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.get(
    `${baseUrl}/get-by-connection?conversationsConnectionId=${request.conversationsConnectionId}`
  );
};

export const getConversationsByRecipientService = (
  request: GetConversationsByRecipientRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.get(`${baseUrl}/get-by-recipient?recipientId=${request.recipientId}`);
};

export const getConversationsTimelineService = (
  request: GetConversationsTimelineRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  const params = new URLSearchParams();
  params.append('conversationId', request.conversationId.toString());
  params.append('sortOrder', 'DESC'); // DESC para obtener los más recientes, frontend hace .reverse()
  if (request.pageSize) params.append('pageSize', request.pageSize.toString());
  if (request.pageNumber) params.append('pageNumber', request.pageNumber.toString());
  return httpWebtrack.get(`${baseUrl}/timeline?${params.toString()}`);
};

// #endregion

// #region Group Members

export const insertGroupMemberService = (
  request: InsertGroupMemberRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/group-members/insert`, request);
};

export const updateGroupMemberService = (
  request: UpdateGroupMemberRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/group-members/update`, request);
};

// #endregion

// #region Inbound Messages

export const insertInboundMessageService = (
  request: InsertInboundMessageRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/messages/inbound/insert`, request);
};

export const updateInboundMessageService = (
  request: UpdateInboundMessageRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/messages/inbound/update`, request);
};

// #endregion

// #region Outbound Messages

export const insertOutboundMessageService = (
  request: InsertOutboundMessageRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/messages/outbound/insert`, request);
};

export const updateOutboundMessageService = (
  request: UpdateOutboundMessageRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/messages/outbound/update`, request);
};

// #endregion

// #region Chat Alerts

export const insertChatAlertService = (
  request: InsertChatAlertRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/alerts/insert`, request);
};

export const updateChatAlertService = (
  request: UpdateChatAlertRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/alerts/update`, request);
};

// #endregion

// #region Call Logs

export const insertCallLogService = (
  request: InsertCallLogRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/calls/insert`, request);
};

export const updateCallLogService = (
  request: UpdateCallLogRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/calls/update`, request);
};

// #endregion

// #region Messages After Last Event

interface GetMessagesAfterLastEventRequest {
  conversationId: number;
  sortOrder?: 'ASC' | 'DESC';
}

export const getMessagesAfterLastEventService = (
  request: GetMessagesAfterLastEventRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/after-last-event`, request);
};

// #endregion

// #region Call Chat Events

export const insertCallChatEventService = (
  request: InsertCallChatEventRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/call-events/insert`, request);
};

export const updateCallChatEventService = (
  request: UpdateCallChatEventRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/call-events/update`, request);
};

// #endregion
// #region WhatsApp Business API

interface SendWhatsAppMessageRequest {
  phoneNumberId: string;
  accessToken: string;
  recipientPhoneNumber: string;
  messageContent: string;
  replyToMessageId?: string;
  mediaUrl?: string;
  mediaType?: 'image' | 'video' | 'audio' | 'document' | 'sticker';
  mediaFileTitle?: string;
}

interface GetWhatsAppTemplatesRequest {
  wabaId: string;
  accessToken: string;
}

interface SendWhatsAppTemplateRequest {
  phoneNumberId: string;
  accessToken: string;
  recipientPhoneNumber: string;
  templateName: string;
  languageCode: string;
  components?: any[];
}

export const sendWhatsAppMessageService = async (
  request: SendWhatsAppMessageRequest
): Promise<{ success: boolean; whatsAppMessageId?: string; error?: string }> => {
  try {
    const url = `https://graph.facebook.com/v18.0/${request.phoneNumberId}/messages`;

    // Limpiar el número: solo quitar espacios, mantener el +
    const cleanPhoneNumber = request.recipientPhoneNumber.replace(/\s/g, '');

    let payload: any = {
      messaging_product: 'whatsapp',
      recipient_type: 'individual',
      to: cleanPhoneNumber
    };

    // Si tiene archivo multimedia
    if (request.mediaUrl && request.mediaType) {
      payload.type = request.mediaType;

      // Configurar según el tipo de media
      if (request.mediaType === 'sticker') {
        payload.sticker = {
          link: request.mediaUrl
        };
      } else if (request.mediaType === 'image') {
        payload.image = {
          link: request.mediaUrl,
          caption: request.messageContent || undefined
        };
      } else if (request.mediaType === 'video') {
        payload.video = {
          link: request.mediaUrl,
          caption: request.messageContent || undefined
        };
      } else if (request.mediaType === 'audio') {
        payload.audio = {
          link: request.mediaUrl
        };
      } else if (request.mediaType === 'document') {
        payload.document = {
          link: request.mediaUrl,
          caption: request.messageContent || undefined,
          filename: request.mediaFileTitle || 'document'
        };
      }
    } else {
      // Mensaje de texto normal
      payload.type = 'text';
      payload.text = {
        preview_url: false,
        body: request.messageContent
      };
    }

    // Si es respuesta a un mensaje
    if (request.replyToMessageId) {
      payload.context = {
        message_id: request.replyToMessageId
      };
    }

    console.log('📤 [WhatsApp API] Sending payload:', {
      url,
      payload,
      hasMedia: !!request.mediaUrl,
      mediaType: request.mediaType,
      mediaUrl: request.mediaUrl
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${request.accessToken}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    console.log('📥 [WhatsApp API] Response:', {
      status: response.status,
      ok: response.ok,
      data
    });

    if (response.ok && data.messages && data.messages[0]?.id) {
      return {
        success: true,
        whatsAppMessageId: data.messages[0].id
      };
    } else {
      console.error('[WhatsApp API] ❌ Error al enviar mensaje:', {
        status: response.status,
        statusText: response.statusText,
        errorData: data,
        sentUrl: request.mediaUrl
      });
      return {
        success: false,
        error: data.error?.message || 'Error desconocido al enviar mensaje'
      };
    }
  } catch (error: any) {
    console.error('[WhatsApp API] ❌ Excepción:', error);
    return {
      success: false,
      error: error.message || 'Error de conexión con WhatsApp Business API'
    };
  }
};

export const getWhatsAppTemplatesService = async (
  request: GetWhatsAppTemplatesRequest
): Promise<any> => {
  try {
    const url = `https://graph.facebook.com/v21.0/${request.wabaId}/message_templates?fields=name,status,language,components,category`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${request.accessToken}`
      }
    });

    const data = await response.json();

    if (response.ok && data.data) {
      // Filtrar por APPROVED (case-insensitive por si acaso)
      const approvedTemplates = data.data.filter(
        (t: any) => t.status && t.status.toUpperCase() === 'APPROVED'
      );

      return {
        success: true,
        templates: approvedTemplates
      };
    } else {
      console.error('[WhatsApp API] Error en respuesta de plantillas:', {
        status: response.status,
        error: data.error
      });
      return {
        success: false,
        error: data.error?.message || 'Error al obtener plantillas'
      };
    }
  } catch (error: any) {
    console.error('[WhatsApp API] Error:', error);
    return {
      success: false,
      error: error.message || 'Error de conexión con WhatsApp Business API'
    };
  }
};

export const sendWhatsAppTemplateService = async (
  request: SendWhatsAppTemplateRequest
): Promise<any> => {
  try {
    const url = `https://graph.facebook.com/v21.0/${request.phoneNumberId}/messages`;

    const cleanPhoneNumber = request.recipientPhoneNumber.replace(/[^0-9]/g, '');

    const payload: any = {
      messaging_product: 'whatsapp',
      recipient_type: 'individual',
      to: cleanPhoneNumber,
      type: 'template',
      template: {
        name: request.templateName,
        language: {
          code: request.languageCode
        }
      }
    };

    if (request.components && request.components.length > 0) {
      payload.template.components = request.components;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${request.accessToken}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (response.ok && data.messages && data.messages[0]?.id) {
      return {
        success: true,
        whatsAppMessageId: data.messages[0].id
      };
    } else {
      console.error('[WhatsApp API] ❌ Error al enviar template:', {
        status: response.status,
        statusText: response.statusText,
        errorData: data
      });
      return {
        success: false,
        error: data.error?.message || 'Error desconocido al enviar plantilla'
      };
    }
  } catch (error: any) {
    console.error('[WhatsApp API] ❌ Excepción:', error);
    return {
      success: false,
      error: error.message || 'Error de conexión con WhatsApp Business API'
    };
  }
};

// #endregion

// #region WhatsApp Calls

interface CheckCallPermissionRequest {
  phoneNumberId: string;
  accessToken: string;
  recipientPhoneNumber: string;
}

export const checkWhatsAppCallPermissionService = async (
  request: CheckCallPermissionRequest
): Promise<{ hasPermission: boolean; error?: string; errorCode?: number }> => {
  try {
    const url = `https://graph.facebook.com/v18.0/${request.phoneNumberId}/calls`;

    // Limpiar el número: solo quitar espacios, mantener el +
    const cleanPhoneNumber = request.recipientPhoneNumber.replace(/\s/g, '');

    // Payload de prueba para verificar permisos (sin SDP real, solo para check)
    const payload = {
      messaging_product: 'whatsapp',
      to: cleanPhoneNumber,
      action: 'connect',
      session: {
        sdp_type: 'offer',
        sdp: 'v=0' // SDP mínimo solo para verificar permisos
      }
    };

    console.log('📞 [WhatsApp Calls] Checking call permission:', {
      url,
      to: cleanPhoneNumber
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${request.accessToken}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    console.log('📥 [WhatsApp Calls] Response:', {
      status: response.status,
      ok: response.ok,
      data
    });

    if (response.ok) {
      // Tiene permiso
      return {
        hasPermission: true
      };
    } else {
      // Error - verificar si es por falta de permisos
      const errorCode = data.error?.code;
      const errorMessage = data.error?.message || 'Error al verificar permisos de llamada';

      // Código 138006 = usuario no tiene permiso de llamada
      if (errorCode === 138006) {
        return {
          hasPermission: false,
          error: 'El usuario no tiene permiso para recibir llamadas de este número',
          errorCode
        };
      }

      return {
        hasPermission: false,
        error: errorMessage,
        errorCode
      };
    }
  } catch (error: any) {
    console.error('[WhatsApp Calls] ❌ Excepción:', error);
    return {
      hasPermission: false,
      error: error.message || 'Error de conexión con WhatsApp Business API'
    };
  }
};

// #endregion

// #region WhatsApp Calls

interface InitiateWhatsAppCallRequest {
  phoneNumberId: string;
  accessToken: string;
  recipientPhoneNumber: string;
  sdpOffer: string;
  trackingData?: string;
}

interface TerminateWhatsAppCallRequest {
  phoneNumberId: string;
  accessToken: string;
  callId: string;
}

/**
 * Inicia llamada de WhatsApp usando Graph API v18.0
 *
 * @param request - Parámetros de la llamada (phoneNumberId, accessToken, recipientPhone, SDP offer)
 * @returns Objeto con success, callId (si exitoso), error y errorCode (si falla)
 *
 * Códigos de error comunes:
 * - 138006: Usuario no tiene permiso para recibir llamadas
 */
export const initiateWhatsAppCallService = async (
  request: InitiateWhatsAppCallRequest
): Promise<{ success: boolean; callId?: string; error?: string; errorCode?: number }> => {
  try {
    const url = `https://graph.facebook.com/v18.0/${request.phoneNumberId}/calls`;
    const cleanPhoneNumber = request.recipientPhoneNumber.replace(/\s/g, '');

    const payload: any = {
      messaging_product: 'whatsapp',
      to: cleanPhoneNumber,
      action: 'connect',
      session: {
        sdp_type: 'offer',
        sdp: request.sdpOffer
      }
    };

    if (request.trackingData) {
      payload.biz_opaque_callback_data = request.trackingData;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${request.accessToken}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (response.ok && data.calls && data.calls[0]?.id) {
      return {
        success: true,
        callId: data.calls[0].id
      };
    } else {
      const errorCode = data.error?.code;
      const errorMessage = data.error?.message || 'Error al iniciar llamada';

      if (errorCode === 138006) {
        return {
          success: false,
          error: 'El usuario no tiene permiso para recibir llamadas de este número',
          errorCode
        };
      }

      return {
        success: false,
        error: errorMessage,
        errorCode
      };
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Error de conexión con WhatsApp Business API'
    };
  }
};

/**
 * Termina llamada activa de WhatsApp
 *
 * @param request - phoneNumberId, accessToken y callId
 * @returns Objeto con success y error (si falla)
 */
export const terminateWhatsAppCallService = async (
  request: TerminateWhatsAppCallRequest
): Promise<{ success: boolean; error?: string }> => {
  try {
    const url = `https://graph.facebook.com/v18.0/${request.phoneNumberId}/calls`;

    const payload = {
      messaging_product: 'whatsapp',
      call_id: request.callId,
      action: 'terminate'
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${request.accessToken}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (response.ok && data.success) {
      return { success: true };
    } else {
      return {
        success: false,
        error: data.error?.message || 'Error al finalizar llamada'
      };
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Error de conexión con WhatsApp Business API'
    };
  }
};

// #endregion

// #region Route Monitoring

interface SearchDriversByPhoneRequest {
  phoneNumber: string;
}

interface GetDriverRoutesRequest {
  driverId: number;
}

interface TriggerRouteCallRequest {
  routeAssignmentId: number;
  driverId: number;
  driverName: string;
  phoneNumbers: Array<{
    phoneNumber: string;
    isVerified: boolean;
  }>;
  partnerId: number;
  routeStatus: number;
  routeStatusName: string;
  unitId: number;
  unitName: string;
  routeId: number;
  routeName: string;
  delayMinutes?: number;
}

export const searchDriversByPhoneService = (
  request: SearchDriversByPhoneRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post('/driver/search-by-phone', request);
};

export const getDriverRoutesService = (
  request: GetDriverRoutesRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post('/route/get-driver-routes', request);
};

export const triggerRouteCallService = (
  request: TriggerRouteCallRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post('/conversations/routes/trigger-call', request);
};

// #endregion

// #region File Upload

export const uploadFileService = (file: File, mediaType: string, configActions: ConfigActions) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('path', `conversations/${mediaType}s/`);
  formData.append('saveInDatabase', 'false');
  formData.append('setContentType', 'true');

  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post('/Files/saveFile', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    timeout: 180000
  });
};

// #endregion
